import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from "recharts";
import { MapPin, Building, TrendingUp, Euro } from "lucide-react";

const RISK_COLORS = {
  verde: "#22c55e",
  medio: "#eab308",
  forte: "#f97316",
  molto_forte: "#ef4444"
};

const CHART_COLORS = ["#0f172a", "#f97316", "#3b82f6", "#10b981", "#ef4444", "#8b5cf6", "#ec4899"];

const formatCurrency = (value) => {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const AnalyticsSede = ({ kpi, preventivi }) => {
  // Prepare Sede data
  const sedeData = useMemo(() => {
    if (!kpi?.by_sede) return [];
    
    return Object.entries(kpi.by_sede)
      .map(([sede, data]) => {
        const total = data.count;
        const riskScore = total > 0 ? 
          ((data.risk.medio * 1 + data.risk.forte * 2 + data.risk.molto_forte * 3) / total).toFixed(2) : 0;
        
        return {
          name: sede.length > 15 ? sede.substring(0, 15) + "..." : sede,
          fullName: sede,
          preventivi: data.count,
          valore: data.value,
          valoreMila: data.value / 1000,
          avgValue: data.count > 0 ? data.value / data.count : 0,
          verde: data.risk.verde,
          medio: data.risk.medio,
          forte: data.risk.forte,
          molto_forte: data.risk.molto_forte,
          riskScore: parseFloat(riskScore),
          riskPercent: total > 0 ? ((data.risk.forte + data.risk.molto_forte) / total * 100).toFixed(1) : 0,
          verdePercent: total > 0 ? (data.risk.verde / total * 100).toFixed(0) : 0
        };
      })
      .sort((a, b) => b.valore - a.valore);
  }, [kpi]);

  // Value distribution for pie chart
  const pieData = useMemo(() => {
    return sedeData.slice(0, 6).map((sede, i) => ({
      name: sede.name,
      value: sede.valore,
      color: CHART_COLORS[i % CHART_COLORS.length]
    }));
  }, [sedeData]);

  // Radar chart data for comparison
  const radarData = useMemo(() => {
    if (sedeData.length === 0) return [];
    
    const maxPreventivi = Math.max(...sedeData.map(s => s.preventivi));
    const maxValore = Math.max(...sedeData.map(s => s.valore));
    
    return sedeData.slice(0, 5).map(sede => ({
      name: sede.name,
      preventivi: (sede.preventivi / maxPreventivi * 100).toFixed(0),
      valore: (sede.valore / maxValore * 100).toFixed(0),
      efficienza: sede.verdePercent,
      avgValue: sede.avgValue > 0 ? Math.min((sede.avgValue / 50000 * 100), 100).toFixed(0) : 0
    }));
  }, [sedeData]);

  if (!kpi || sedeData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400">
        Nessun dato disponibile
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="analytics-sede-page">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-slate-900">
          Analisi per Sede
        </h2>
        <Badge variant="outline" className="text-slate-600">
          {sedeData.length} Sedi Attive
        </Badge>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-100">
                <Building className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Sedi Totali</p>
                <p className="text-xl font-bold font-heading">{sedeData.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-100">
                <MapPin className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Sede Top</p>
                <p className="text-lg font-bold font-heading truncate max-w-[150px]" title={sedeData[0]?.fullName}>
                  {sedeData[0]?.name || "-"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <Euro className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Valore Medio Sede</p>
                <p className="text-lg font-bold font-heading">
                  {formatCurrency(kpi.total_value / sedeData.length)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <TrendingUp className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Prev. Medi/Sede</p>
                <p className="text-xl font-bold font-heading">
                  {Math.round(kpi.total_preventivi / sedeData.length)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Value Distribution Bar */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg uppercase tracking-tight">
              Valore per Sede
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sedeData} margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                  <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k€`} />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), "Valore"]}
                    contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
                  />
                  <Bar dataKey="valore" radius={[4, 4, 0, 0]}>
                    {sedeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Value Pie Chart */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg uppercase tracking-tight">
              Distribuzione Valore (Top 6)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={false}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), "Valore"]}
                    contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Risk by Sede Stacked */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg uppercase tracking-tight">
              Distribuzione Rischio per Sede
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sedeData} margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                  <YAxis />
                  <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }} />
                  <Legend />
                  <Bar dataKey="verde" stackId="a" fill={RISK_COLORS.verde} name="Verde" />
                  <Bar dataKey="medio" stackId="a" fill={RISK_COLORS.medio} name="Medio" />
                  <Bar dataKey="forte" stackId="a" fill={RISK_COLORS.forte} name="Forte" />
                  <Bar dataKey="molto_forte" stackId="a" fill={RISK_COLORS.molto_forte} name="Molto Forte" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Radar Comparison */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg uppercase tracking-tight">
              Confronto Sedi (Radar)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart cx="50%" cy="50%" outerRadius="70%" data={[
                  { subject: "Preventivi", ...Object.fromEntries(radarData.map(r => [r.name, r.preventivi])) },
                  { subject: "Valore", ...Object.fromEntries(radarData.map(r => [r.name, r.valore])) },
                  { subject: "Efficienza", ...Object.fromEntries(radarData.map(r => [r.name, r.efficienza])) },
                  { subject: "Media €", ...Object.fromEntries(radarData.map(r => [r.name, r.avgValue])) }
                ]}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} />
                  {radarData.map((sede, i) => (
                    <Radar 
                      key={sede.name}
                      name={sede.name} 
                      dataKey={sede.name} 
                      stroke={CHART_COLORS[i]} 
                      fill={CHART_COLORS[i]} 
                      fillOpacity={0.2} 
                    />
                  ))}
                  <Legend />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sede Detail Table */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg uppercase tracking-tight">
            Dettaglio Completo Sedi
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sede-detail-table">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold">Sede</th>
                  <th className="text-center py-3 px-4 font-semibold">Preventivi</th>
                  <th className="text-right py-3 px-4 font-semibold">Valore Totale</th>
                  <th className="text-right py-3 px-4 font-semibold">Media</th>
                  <th className="text-center py-3 px-4 font-semibold text-green-600">Verde</th>
                  <th className="text-center py-3 px-4 font-semibold text-yellow-600">Medio</th>
                  <th className="text-center py-3 px-4 font-semibold text-orange-600">Forte</th>
                  <th className="text-center py-3 px-4 font-semibold text-red-600">M. Forte</th>
                  <th className="text-center py-3 px-4 font-semibold">% Rischio</th>
                </tr>
              </thead>
              <tbody>
                {sedeData.map((sede, i) => (
                  <tr key={sede.fullName} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 font-medium" title={sede.fullName}>{sede.name}</td>
                    <td className="py-3 px-4 text-center">{sede.preventivi}</td>
                    <td className="py-3 px-4 text-right font-medium">{formatCurrency(sede.valore)}</td>
                    <td className="py-3 px-4 text-right">{formatCurrency(sede.avgValue)}</td>
                    <td className="py-3 px-4 text-center text-green-600">{sede.verde}</td>
                    <td className="py-3 px-4 text-center text-yellow-600">{sede.medio}</td>
                    <td className="py-3 px-4 text-center text-orange-600">{sede.forte}</td>
                    <td className="py-3 px-4 text-center text-red-600">{sede.molto_forte}</td>
                    <td className="py-3 px-4 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        parseFloat(sede.riskPercent) > 50 ? "bg-red-100 text-red-700" :
                        parseFloat(sede.riskPercent) > 25 ? "bg-orange-100 text-orange-700" :
                        "bg-green-100 text-green-700"
                      }`}>
                        {sede.riskPercent}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsSede;
